import { useEffect, useState } from 'react';
import type { FakeItem } from '../data/types';
import FakePage from './FakePage';

/* Shown after REVEAL on a FAKE headline: the fake page full-screen with the FAKE stamp over
   it, held ~2.6s, then the stamp dissolves and leaves the page on screen to react to.

   Unlike the REAL path this never leaves the app — PlayView pushed a history entry, so Back
   (or Esc) closes it and returns to the headline. */

const FADE_AT_MS = 2600;

interface Props {
  item: FakeItem;
  ribbonLabel?: string;
  headlineSize?: string;
  showWordmark?: boolean;
  stampUrl?: string | null;
  onClose: () => void;
}

export default function FakeReveal({ item, ribbonLabel = '', headlineSize, showWordmark = true, stampUrl, onClose }: Props) {
  const [fading, setFading] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setFading(true), FADE_AT_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fake-reveal" data-testid="fake-reveal">
      {/* A broken-image icon must never reach camera: a failed load falls back to the branded
          "graphic not found" page. Only a full-bleed design has nothing else to show — a
          composed page still has its headline and copy, so one dead image does not blank it. */}
      <FakePage
        item={item}
        ribbonLabel={ribbonLabel}
        headlineSize={headlineSize}
        showWordmark={showWordmark}
        missing={missing}
        onImageError={() => {
          if (item.images.length === 1) setMissing(true);
        }}
      />

      <div className={`stamp-overlay${fading ? ' fading' : ''}`}>
        {stampUrl ? (
          <img className="stamp-img" src={stampUrl} alt="FAKE" />
        ) : (
          <div className="stamp fake">FAKE</div>
        )}
      </div>
      <div className="escape-hint">Esc or Back = return to the game</div>
    </div>
  );
}
